import { useI18n } from "../../../lib/useI18n.js";
import { COLOR_LABEL_KEYS, TASK_COLOR_PALETTE } from "../../task-colors.js";

interface Props {
  value: string | null;
  onChange: (v: string | null) => void;
}

/** Inline round color swatches (app palette + native OS color picker).
 *  The rainbow swatch is a <label> wrapping a real <input type="color"> — tapping
 *  the label opens the platform's own color picker (the reliable cross-browser
 *  trigger; a programmatic `input.click()` is blocked by some Android WebViews). */
export function ColorSwatchRow({ value, onChange }: Props) {
  const { t } = useI18n();
  const isCustom = value !== null && !TASK_COLOR_PALETTE.some((c) => c.value === value);

  return (
    <div className="color-inline">
      <div className="swatches">
        {TASK_COLOR_PALETTE.map((c) => {
          const label = COLOR_LABEL_KEYS[c.label] ? t(COLOR_LABEL_KEYS[c.label]!) : c.label;
          return (
            <button
              key={c.label}
              type="button"
              className={
                "swatch" +
                (c.value === value ? " is-selected" : "") +
                (c.value === null ? " is-none" : "")
              }
              style={{ ["--sw" as string]: c.value ?? "transparent" }}
              onClick={() => onChange(c.value)}
              title={label}
              aria-label={label}
            />
          );
        })}
        <label
          className={"swatch is-rainbow" + (isCustom ? " is-selected" : "")}
          title={t("color.custom")}
          aria-label={t("color.custom")}
        >
          <input
            type="color"
            value={isCustom ? value! : "#ffffff"}
            onChange={(e) => onChange(e.target.value)}
            className="rainbow-input"
            aria-label={t("color.pickCustom")}
          />
        </label>
      </div>
    </div>
  );
}
